import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useContext, useMemo } from 'react';
import { PlusCircle, X } from 'react-feather';
import styled from '@emotion/styled';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import type { BrandInsightsPageContext } from '@trinity/chat';
import { ChatContextType } from '@trinity/chat';
import { AuraChatContainer } from '@trinity/chat-components/components/BrandInsights/AuraChat.Container.js';
import {
  ChatContextSelectorVisibleContext,
  ChatHomeButtonVisibleContext,
  SidePanelExpandedContext,
} from '@trinity/chat-components/components/BrandInsights/context.js';
import { ChatInstance } from '@trinity/chat-components/components/Chat/Chat.js';
import {
  ChatInputFeaturesContext,
  ChatLandingCardsVisibleContext,
  ChatPageContextDataContext,
} from '@trinity/chat-components/components/Chat/context.js';
import { ChatHeader } from '@trinity/chat-components/components/Chat.Header/index.js';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context.js';
import { resetContextSubject } from '@trinity/chat-components/components/Chat.State/state.chat-session.js';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier.js';
import { useAppSelector } from '@/store';

const noop: Dispatch<SetStateAction<boolean>> = () => {};

/**
 * Presentation component for the native chat sidebar.
 *
 * Expects `ChatContextProviders` (or equivalent) to be mounted above it so
 * that `ChatPageContextDataContext`, `ChatStateContainer`, etc. are available.
 */
export function ChatInterfaceNative({
  disableNewChat,
  onClose,
}: {
  disableNewChat?: boolean;
  onClose?: () => void;
}): React.JSX.Element {
  const canToggleAuraChat = useAppSelector((s) => s.features.chatAuraIframesEnabled);
  const localContextData = useContext(ChatPageContextDataContext);

  const sidePanelExpandedValue = useMemo<[boolean, Dispatch<SetStateAction<boolean>>]>(
    () => [true, noop],
    []
  );

  const inputFeaturesValue = useMemo(() => ({ fileUpload: false, voiceInput: true }) as const, []);

  return (
    <AuraChatContainer canToggleAuraChat={canToggleAuraChat}>
      <ChatInputFeaturesContext.Provider value={inputFeaturesValue}>
        <ChatLandingCardsVisibleContext.Provider value={false}>
          <SidePanelExpandedContext.Provider value={sidePanelExpandedValue}>
            <ChatHomeButtonVisibleContext.Provider value={false}>
              <ChatContextSelectorVisibleContext.Provider value={false}>
                <PageHeader>
                  <StyledAutoAwesomeIcon />
                  <PageHeaderTitle>Discover Insights</PageHeaderTitle>
                  {onClose && (
                    <CloseBtn onClick={onClose} title="Hide chat">
                      <X size={16} />
                    </CloseBtn>
                  )}
                </PageHeader>
                <HeaderRow>
                  <ChatHeader disableContextDisplay={true} disableTabs={true} />
                  {localContextData && !disableNewChat && <NewChatButton agentContext={localContextData} />}
                </HeaderRow>
                <StyledChatInstance isSidePanel />
              </ChatContextSelectorVisibleContext.Provider>
            </ChatHomeButtonVisibleContext.Provider>
          </SidePanelExpandedContext.Provider>
        </ChatLandingCardsVisibleContext.Provider>
      </ChatInputFeaturesContext.Provider>
    </AuraChatContainer>
  );
}

const StyledChatInstance = styled(ChatInstance)({
  height: `calc(100% - 90px)`, // Estimate based on header + padding, adjust as needed
});

function NewChatButton({ agentContext }: { agentContext: BrandInsightsPageContext.Item }): React.JSX.Element {
  const dispatch = useContext(ChatStateDispatchContext);
  const pageContext = useContext(ChatPageContextDataContext);
  const brandIdentifier = useActiveBrandIdentifier();

  const handleNewChat = useCallback(() => {
    const context = pageContext ?? agentContext;
    const resetLandingCards =
      context.type === ChatContextType.Agent && context.data.showLandingCardsOnNewChat;

    dispatch({
      type: 'new-chat-started',
      payload: {
        context: resetContextSubject(context, resetLandingCards),
        brandIdentifier,
        resetLandingCards,
      },
    });
  }, [dispatch, pageContext, agentContext, brandIdentifier]);

  return (
    <Tooltip title="New Chat">
      <StyledNewChatButton onClick={handleNewChat}>
        <PlusCircle size={20} />
      </StyledNewChatButton>
    </Tooltip>
  );
}

const StyledAutoAwesomeIcon = styled(AutoAwesomeIcon)(({ theme }) => ({
  fontSize: 20,
  color: theme.palette.primary.main,
}));

const PageHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 8px 0',
  marginBottom: '8px',
});

const PageHeaderTitle = styled.span({
  fontSize: 16,
  fontWeight: 600,
  color: '#1a1a1a',
  flex: 1,
});

const HeaderRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  '& > *:first-child': {
    flex: 1,
    minWidth: 0,
  },
});

const CloseBtn = styled.button(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  color: theme.palette.gray[400],
  padding: '4px',
  borderRadius: '4px',
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.gray[100],
    color: theme.palette.gray[700],
  },
}));

const StyledNewChatButton = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: '6px',
  marginRight: '8px',
  borderRadius: '4px',
  color: theme.palette.primary.main,
  transition: 'background-color 0.2s ease',

  '&:hover': {
    backgroundColor: theme.palette.gray.light2,
  },
}));
